/**
 * @file alert 警告提示
 * @author jiangting01
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import CloseOutlined from 'acud-icon/lib/icons/OutlinedClose';
import CheckCircleFilled from 'acud-icon/lib/icons/FilledSuccess';
import ExclamationCircleFilled from 'acud-icon/lib/icons/FilledWarn';
import InfoCircleFilled from 'acud-icon/lib/icons/FilledInfo';
import CloseCircleFilled from 'acud-icon/lib/icons/FilledError';
import CSSMotion from 'rc-motion';
import classNames from 'classnames';
import { ConfigContext } from '../config-provider';
import getDataOrAriaProps from '../_util/getDataOrAriaProps';
import { replaceElement } from '../_util/reactNode';
import ErrorBoundary from './ErrorBoundary';
const iconMapping = {
    success: { icon: CheckCircleFilled, color: '#30BF13' },
    info: { icon: InfoCircleFilled, color: '#2468F2' },
    error: { icon: CloseCircleFilled, color: '#F33E3E' },
    warning: { icon: ExclamationCircleFilled, color: '#FF9326' },
};
const Alert = (_a) => {
    var { description, prefixCls: customizePrefixCls, message, banner, className = '', style, onMouseEnter, onMouseLeave, onClick, afterClose, showIcon, closable, closeText, action } = _a, props = __rest(_a, ["description", "prefixCls", "message", "banner", "className", "style", "onMouseEnter", "onMouseLeave", "onClick", "afterClose", "showIcon", "closable", "closeText", "action"]);
    const [closed, setClosed] = React.useState(false);
    const ref = React.useRef();
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('alert', customizePrefixCls);
    const handleClose = (e) => {
        var _a;
        setClosed(true);
        (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props, e);
    };
    const getType = () => {
        const type = props.type;
        if (type !== undefined) {
            return type;
        }
        // banner 模式默认为警告
        return banner ? 'warning' : 'info';
    };
    // closeable when closeText is assigned
    const isClosable = closeText ? true : closable;
    const type = getType();
    const renderIconNode = () => {
        const icon = props.icon;
        if (icon) {
            return replaceElement(icon, <span className={`${prefixCls}-icon`}>{icon}</span>, () => ({
                className: classNames(`${prefixCls}-icon`, {
                    [icon.props.className]: icon.props.className
                })
            }));
        }
        const iconType = iconMapping[type];
        if (iconType) {
            return React.createElement(iconType.icon, { className: `${prefixCls}-icon`, color: iconType.color });
        }
        return null;
    };
    const renderCloseIcon = () => isClosable ? (<button type="button" onClick={handleClose} className={`${prefixCls}-close-icon`} tabIndex={0}>
                {closeText ? (<span className={`${prefixCls}-close-text`}>{closeText}</span>) : (<CloseOutlined />)}
            </button>) : null;
    // banner 模式默认有 Icon
    const isShowIcon = banner && showIcon === undefined ? true : showIcon;
    const alertCls = classNames(prefixCls, `${prefixCls}-${type}`, {
        [`${prefixCls}-with-description`]: !!description,
        [`${prefixCls}-no-icon`]: !isShowIcon,
        [`${prefixCls}-banner`]: !!banner,
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className);
    const dataOrAriaProps = getDataOrAriaProps(props);
    return (<CSSMotion visible={!closed} motionName={`${prefixCls}-motion`} motionAppear={false} motionEnter={false} onLeaveStart={node => ({
            maxHeight: node.offsetHeight,
        })} onLeaveEnd={afterClose}>
            {({ className: motionClassName, style: motionStyle }) => (<div ref={ref} data-show={!closed} className={classNames(alertCls, motionClassName)} style={Object.assign(Object.assign({}, style), motionStyle)} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick} role="alert" {...dataOrAriaProps}>
                    {isShowIcon ? renderIconNode() : null}
                    <div className={`${prefixCls}-content`}>
                        <div className={`${prefixCls}-message`}>{message}</div>
                        <div className={`${prefixCls}-description`}>{description}</div>
                    </div>

                    {action ? <div className={`${prefixCls}-action`}>{action}</div> : null}

                    {renderCloseIcon()}
                </div>)}
        </CSSMotion>);
};
Alert.ErrorBoundary = ErrorBoundary;
export default Alert;
